import React, { useContext, useEffect, useMemo, useState } from 'react';
import { objectOf, string, func, bool } from 'prop-types';
import { connect } from 'react-redux';
import { Box, Flex } from 'rebass';
import { useParams } from 'react-router-dom';
import { ButtonStyled, TextStyled } from '@nike/nike-design-system-components';

import { TableServerSideContext } from '~COMPONENTS/tableServerSide/TableServerSideContext';
import { showToast } from '../../../components/toast/actions';

import {
  saveAdminDepartmentCatalogTypes as saveCatalogTypes,
  selectAdminAccount,
  updateDepartmentPoPrefix
} from '../actions';

import { getAccountDepartmentColumns } from './accountDepartmentColumns';

import { getAdminAccountsData, getSelectedAccountId } from '../selectors';
import { getAdminHeaderOpenState } from '../../selectors';

import { accountType } from '../propTypes';

import TableServerSide from '../../../components/tableServerSide/TableServerSide';
import AddCatalogTypeModal from './AddCatalogTypeModal';
import RemoveCatalogTypeModal from './RemoveCatalogTypeModal';
import ActionItemList from '../../common/ActionItemList';

import useModal from '../../../hooks/useModal';

const AccountDepartments = ({
  selectAdminAccount,
  selectedAccountId,
  accounts,
  isAdminHeaderOpen,
  updateDepartmentPoPrefix
}) => {
  const { id } = useParams();

  useEffect(() => {
    // force account selection if user refreshes the page
    if (id && !selectedAccountId) {
      selectAdminAccount(id);
    }
  }, []);

  const { totalResources, tableInstance, refreshTableData, handleClearSortAndFilters } =
    useContext(TableServerSideContext);

  const { items: tableData, selectedRowIds = {}, selectedFlatRows } = tableInstance;

  const [departments, setDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedCatalogType, setSelectedCatalogType] = useState({});

  useEffect(() => {
    setDepartments(tableData);
  }, [id, JSON.stringify(tableData)]);

  useEffect(() => {
    refreshTableData();
  }, [selectedAccountId, isAdminHeaderOpen]);

  useEffect(() => {
    setSelectedDepartments(selectedFlatRows?.map(item => item.original) || []);
  }, [selectedFlatRows, JSON.stringify(departments)]);

  const updateDepartmentCatalogTypes = (departmentId, newCatalogTypes) => {
    const departmentsUpdated = departments.map(department =>
      department.id === departmentId ? { ...department, catalogTypes: newCatalogTypes } : department
    );

    setDepartments(departmentsUpdated);
  };

  const bulkUpdateDepartmentCatalogTypes = departmentIdToCatalogTypes => {
    const deptIds = Object.keys(departmentIdToCatalogTypes);
    const departmentsUpdated = departments.map(department => {
      if (deptIds.includes(department.id)) {
        return {
          ...department,
          catalogTypes: departmentIdToCatalogTypes[department.id]
        };
      }
      return department;
    });

    setDepartments(departmentsUpdated);
  };

  const selectedAccount =
    selectedAccountId && Object.values(accounts).find(account => account.id === selectedAccountId);

  const [isAddCatalogTypeModalOpen, openAddCatalogTypeModal, closeAddCatalogTypeModal] = useModal();
  const [isRemoveCatalogTypeModalOpen, openRemoveCatalogTypeModal, closeRemoveCatalogTypeModal] = useModal();

  const memoizedColumns = useMemo(
    () =>
      getAccountDepartmentColumns({
        departments,
        openAddCatalogTypeModal,
        openRemoveCatalogTypeModal,
        setSelectedDepartments,
        setSelectedCatalogType,
        updateDepartmentPoPrefix
      }),
    [JSON.stringify(departments)]
  );

  const actionItems = {
    'Add Catalog Type': { action: openAddCatalogTypeModal }
  };

  return (
    <Box width='100%'>
      <Flex alignItems='center'>
        <TextStyled appearance='body2' style={{ marginLeft: '5px', marginRight: '10px' }}>
          {`${totalResources}`} results
        </TextStyled>

        <ButtonStyled
          appearance='text'
          background='light'
          ariaLabel='Clear All Filters'
          type='button'
          onClick={handleClearSortAndFilters}
          style={{ marginRight: '10px' }}
        >
          Clear All Filters
        </ButtonStyled>

        <ActionItemList selected={selectedRowIds} actionItems={actionItems} />
      </Flex>

      <TableServerSide
        columns={memoizedColumns}
        url={`v1/departments?filter=accountId(${id})`}
        fullWidth
        fullHeight
        itemSize={410}
        includeRowSelect
      />

      <AddCatalogTypeModal
        isOpen={isAddCatalogTypeModalOpen}
        closeModal={closeAddCatalogTypeModal}
        salesOrg={selectedAccount?.salesOrg || ''}
        bulkUpdateDepartmentCatalogTypes={bulkUpdateDepartmentCatalogTypes}
        allDepartments={departments}
        selectDepartmentIds={selectedDepartments.map(dept => dept.id)}
      />

      <RemoveCatalogTypeModal
        isOpen={isRemoveCatalogTypeModalOpen}
        closeModal={closeRemoveCatalogTypeModal}
        department={selectedDepartments?.length ? selectedDepartments[0] : {}}
        selectedCatalogType={selectedCatalogType}
        updateDepartmentCatalogTypes={updateDepartmentCatalogTypes}
      />
    </Box>
  );
};

AccountDepartments.propTypes = {
  accounts: objectOf(accountType).isRequired,
  selectedAccountId: string.isRequired,
  selectAdminAccount: func.isRequired,
  isAdminHeaderOpen: bool,
  updateDepartmentPoPrefix: func.isRequired
};

AccountDepartments.defaultProps = {
  isAdminHeaderOpen: false
};

const mapStateToProps = state => ({
  accounts: getAdminAccountsData(state),
  selectedAccountId: getSelectedAccountId(state),
  isAdminHeaderOpen: getAdminHeaderOpenState(state)
});

const mapDispatchToProps = { saveCatalogTypes, selectAdminAccount, showToast, updateDepartmentPoPrefix };

export default connect(mapStateToProps, mapDispatchToProps)(AccountDepartments);
